import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Container, Grid, Stack, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import Page from 'components/Page';
import DashboardLayout from 'layouts/DashboardLayout/DashboardLayout';
import { TableToolbar } from 'components/TableToolbar';
import useAuthService from 'hooks/useAuthService';
import { IApiError } from 'models';
import useToast from 'hooks/useToast';
import { SearchIcon } from 'components/appIcons';
import CustomDatePicker from 'components/CustomDatePicker';
import { downloadDataCSV, formatDate, mapRecordsToTableData } from 'utils/utils';
import { useFormValues } from 'hooks/useFormValues';
import AppTable, { IAppTableColumn, IAppTableRef, INIT_TABLE_REF } from 'components/AppTable';
import { requiredOnly } from 'utils/validation';
import { IInstitution, IMappedOption } from 'pages/AdminManagement/Institutions/Institutions.model';
import { getInstitutions } from 'pages/AdminManagement/Institutions/Institutions.service';

import { ISearchUssdSessions, ISearchUssdSessionsBody, IUssdSessionInfo } from './UssdSession.model';
import { getUssdSessions } from './UssdSession.servise';
import UssdSessionsActionMenu from './UssdSessionsActionMenu';

const today = new Date();
const dateFormat = 'YYYY-MM-DD';
const endDate = formatDate(today, true, dateFormat);
const startDate = formatDate(new Date(today.getFullYear(), today.getMonth() - 6), false, dateFormat);

const schema = yup.object().shape({
  endDate: requiredOnly('endDate'),
  startDate: requiredOnly('startDate'),
  phoneNumber: yup.string(),
});

const UssdSessions = () => {
  const { isISWUser, authUser } = useAuthService();
  const { t } = useTranslation(['labels']);

  const { values, updateValue, updateValueByName, errors, setExtErrors, touched, setTouched } =
    useFormValues<ISearchUssdSessions>(
      {
        startDate,
        endDate,
        phoneNumber: '',
      },
      schema
    );
  const { showToast } = useToast();
  const [fetching, setFetching] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [recordsCount, setRecordsCount] = useState(0);
  const [institutions, setInstitutions] = useState<IInstitution[]>([]);
  const [mappedInst, setMappedInst] = useState<IMappedOption[]>([]);
  const [ussdSessions, setUssdSessions] = useState<IUssdSessionInfo[]>([]);

  const appTableRef = useRef<IAppTableRef>(INIT_TABLE_REF);

  const columns: IAppTableColumn<IUssdSessionInfo>[] = useMemo(() => {
    return (
      [
        { name: t('labels:createDate'), prop: 'date' },
        { name: t('labels:query'), prop: 'query' },
        { name: t('labels:sessionId'), prop: 'sessionId' },
        { name: t('labels:msisdn'), prop: 'phoneNumber' },
        { name: t('labels:telco'), prop: 'telco' },
        { name: t('labels:debit'), prop: 'debitSum' },
        { name: t('labels:messageCount'), prop: 'messageCount' },
        { name: t('labels:billingUnits'), prop: 'billingUnits' },
        {
          name: '',
          prop: 'id',
          renderCell: (id, ussdSession) => <UssdSessionsActionMenu id={id} ussdSession={ussdSession} />,
          bodyCellProps: { align: 'right' },
        },
      ] as (null | IAppTableColumn<IUssdSessionInfo>)[]
    ).filter(Boolean) as IAppTableColumn<IUssdSessionInfo>[];
  }, [isISWUser]);

  const fetchInstitutions = useCallback(() => {
    getInstitutions({ pageNumber: 1 })
      .then((instData) => setInstitutions(instData.institutions))
      .catch((err: IApiError) => {
        showToast({
          message: err.description,
          type: 'error',
          hideAfter: null,
          buttons: [
            {
              text: 'Retry',
              onClick: (close) => {
                fetchInstitutions();
                close();
              },
            },
          ],
        });
      });
  }, []);

  useEffect(() => {
    fetchInstitutions();
  }, []);

  useEffect(() => {
    if (institutions.length) {
      const mappedArr: IMappedOption[] | any[] = isISWUser ? [{ label: 'All', id: 'All', code: 'All' }] : [];
      institutions.map((inst) => {
        if (isISWUser || inst.name === authUser.institution.name) {
          mappedArr.push({ label: inst.name, id: inst.id, code: inst.code });
        }
      });
      setMappedInst([...mappedArr]);
    }
  }, [institutions]);

  const buildSearchBody = useCallback(
    (pageNumber?: number, pageSize?: number): ISearchUssdSessionsBody => {
      const tbd = appTableRef.current;
      return {
        pageNumber: pageNumber || tbd.pageNumber + 1,
        pageSize: pageSize || tbd.pageSize,
        ...values,
        //institutionCode: values.institutionCode === 'All' ? '' : values.institutionCode,
      };
    },
    [values]
  );

  const searchUssdSessions = useCallback(
    (pageNumber?: number) => {
      const reqBody = buildSearchBody(pageNumber);

      setFetching(true);
      getUssdSessions(reqBody)
        .then((data) => {
          setRecordsCount(data.totalRecordCount);
          appTableRef.current.setPageNumber(reqBody.pageNumber - 1);
          setUssdSessions(data.sessionInfos);
        })
        .catch((err: IApiError) => {
          setRecordsCount(0);
          setUssdSessions([]);
          showToast({ message: err.description, type: 'error' });
        })
        .finally(() => setFetching(false));
    },
    [buildSearchBody]
  );

  const syncTable = useCallback(() => searchUssdSessions(), [searchUssdSessions]);

  const onSearchSubmit = useCallback(
    (evt) => {
      evt.preventDefault();
      searchUssdSessions(1);
    },
    [searchUssdSessions]
  );

  const download = useCallback(() => {
    const tbd = appTableRef.current;
    const reqBody = buildSearchBody(1, tbd.recordsCount);
    setDownloading(true);

    getUssdSessions(reqBody)
      .then((data) => {
        downloadDataCSV(mapRecordsToTableData(data.sessionInfos, columns, ['']), 'UssdSessions');
      })
      .catch((err: IApiError) => {
        showToast({ message: err.description, type: 'error' });
      })
      .finally(() => setDownloading(false));
  }, [buildSearchBody]);

  return (
    <DashboardLayout>
      <Page title={t('labels:ussdSessions')}>
        <Container maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
            <Typography variant="h4" gutterBottom>
              {t('labels:ussdSessionData')}
            </Typography>
          </Stack>
          <Card>
            <TableToolbar>
              <form style={{ width: '100%' }} onSubmit={onSearchSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <CustomDatePicker
                      name="startDate"
                      label="startDate"
                      value={values.startDate}
                      updateValueByName={updateValueByName}
                      touched={touched.startDate}
                      setTouched={setTouched}
                      error={errors?.startDate}
                      setExtErrors={setExtErrors}
                      dateFormat={dateFormat}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <CustomDatePicker
                      name="endDate"
                      label="endDate"
                      value={values.endDate}
                      updateValueByName={updateValueByName}
                      touched={touched.endDate}
                      setTouched={setTouched}
                      error={errors?.endDate}
                      setExtErrors={setExtErrors}
                      dateFormat={dateFormat}
                      minDate={values.startDate ? new Date(values.startDate) : undefined}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      name="phoneNumber"
                      label={t('labels:phoneNumber')}
                      value={values.phoneNumber}
                      onChange={updateValue}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button type="submit" variant="contained" size="large" disabled={fetching || Boolean(errors)}>
                      <SearchIcon /> {t('labels:search')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </TableToolbar>
            <AppTable
              data={ussdSessions}
              columns={columns}
              keyProp="sessionId"
              syncTable={syncTable}
              recordsCount={recordsCount}
              loading={fetching}
              ref={appTableRef}
              actions={
                ussdSessions?.length ? (
                  <LoadingButton loading={downloading} onClick={download}>
                    Download
                  </LoadingButton>
                ) : null
              }
            />
          </Card>
        </Container>
      </Page>
    </DashboardLayout>
  );
};

export default UssdSessions;
